import type { AccountValidationRules, PaymentType } from '@/services';
import type { CapitalGlobalProposalStepPagamentoValues } from '@/features/proposals/schemas/capital-global/proposal-step-pagamento.schema';
import type { BankMaskConfig } from '@/shared/utils/bank-account.helper';
import {
  resolveBankKey,
  resolveBankMaskConfig,
  validateBankAccountDigit,
  validateBankAgencyDigit,
} from '@/shared/utils/bank-account.helper';
import {
  getPaymentTypeByCode,
  isDebitPaymentType,
} from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-payment-types.hook';

export type CapitalGlobalBankFieldName =
  | 'agency'
  | 'agencyDigit'
  | 'account'
  | 'accountDigit';

type BankFieldValues = Pick<
  CapitalGlobalProposalStepPagamentoValues,
  CapitalGlobalBankFieldName | 'paymentMethod'
>;

function isDigits(value: string): boolean {
  return /^\d+$/.test(value);
}

function validateAccountWithRules(
  rules: AccountValidationRules | undefined,
  agencyNumber: string,
  agencyDigit: string,
  accountNumber: string,
  accountDigit: string
): {
  agencyNumberValid: boolean;
  agencyValid: boolean;
  accountNumberValid: boolean;
  accountValid: boolean;
} {
  if (!rules) {
    return {
      agencyNumberValid: true,
      agencyValid: true,
      accountNumberValid: true,
      accountValid: true,
    };
  }

  const agencyNumberMatchesPattern = /^[0-9]{4,5}$/.test(agencyNumber);
  const agencyNumberIsOnlyZeros = /^0{4,5}$/.test(agencyNumber);
  const accountNumberMatchesPattern = /^[0-9]{5,13}$/.test(accountNumber);
  const accountNumberIsOnlyZeros = /^0{5,13}$/.test(accountNumber);

  return {
    agencyNumberValid: agencyNumberMatchesPattern && !agencyNumberIsOnlyZeros,
    agencyValid: new RegExp(rules.agencyDigitPattern, 'i').test(agencyDigit),
    accountNumberValid:
      accountNumberMatchesPattern && !accountNumberIsOnlyZeros,
    accountValid: new RegExp(rules.accountDigitPattern, 'i').test(accountDigit),
  };
}

export function sanitizeCapitalGlobalBankDigits(
  value: string,
  maxLength?: number
): string {
  const digits = String(value || '').replace(/\D/g, '');
  if (typeof maxLength === 'number') return digits.slice(0, maxLength);
  return digits;
}

export function sanitizeCapitalGlobalBankDigit(
  value: string,
  maxLength?: number
): string {
  const alphanumeric = String(value || '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');
  if (typeof maxLength === 'number') return alphanumeric.slice(0, maxLength);
  return alphanumeric;
}

export function getCapitalGlobalBankMaskConfig(
  paymentMethodCode: string,
  paymentTypes: PaymentType[]
): BankMaskConfig | null {
  const paymentType = getPaymentTypeByCode(paymentMethodCode, paymentTypes);
  if (!paymentType || !isDebitPaymentType(paymentType)) return null;
  return resolveBankMaskConfig(paymentType.description);
}

export function getCapitalGlobalBankFieldLimits(
  fieldName: CapitalGlobalBankFieldName,
  bankMaskConfig: BankMaskConfig | null,
  rules?: AccountValidationRules
): {
  maxLength: number;
  placeholder: string;
  required: boolean;
  enabled: boolean;
} {
  const isAgency = fieldName === 'agency';
  const isAgencyDigit = fieldName === 'agencyDigit';
  const isAccount = fieldName === 'account';

  if (isAgency) {
    return {
      maxLength: bankMaskConfig?.agencyLength ?? 5,
      placeholder: bankMaskConfig?.agencyPlaceholder ?? 'Número da agência',
      required: true,
      enabled: true,
    };
  }

  if (isAgencyDigit) {
    const enabled = bankMaskConfig ? bankMaskConfig.agencyDigitEnabled : true;
    return {
      maxLength: bankMaskConfig?.agencyDigitLength ?? 1,
      placeholder: enabled
        ? (bankMaskConfig?.agencyDigitPlaceholder ?? 'Dígito da agência')
        : 'Não se aplica',
      required: false,
      enabled,
    };
  }

  if (isAccount) {
    return {
      maxLength: bankMaskConfig?.accountLength ?? 13,
      placeholder: bankMaskConfig?.accountPlaceholder ?? 'Número da conta',
      required: true,
      enabled: true,
    };
  }

  return {
    maxLength:
      bankMaskConfig?.accountDigitLength ??
      rules?.accountDigitMaxLength ??
      2,
    placeholder:
      bankMaskConfig?.accountDigitPlaceholder ?? 'Dígito de controle',
    required: true,
    enabled: true,
  };
}

export function validateCapitalGlobalBankField(
  fieldName: CapitalGlobalBankFieldName,
  value: string,
  values: BankFieldValues,
  paymentTypes: PaymentType[]
): string | undefined {
  const paymentType = getPaymentTypeByCode(values.paymentMethod, paymentTypes);
  if (!paymentType || !isDebitPaymentType(paymentType)) return undefined;

  const bankMaskConfig = resolveBankMaskConfig(paymentType.description);
  const rules = paymentType.accountValidationRules;
  const raw = String(value || '').trim();
  const limits = getCapitalGlobalBankFieldLimits(
    fieldName,
    bankMaskConfig,
    rules
  );

  if (fieldName === 'agency') {
    if (!raw) return 'Informe a agência';
    if (!isDigits(raw)) return 'A agência deve conter apenas números';
    return undefined;
  }

  if (fieldName === 'agencyDigit') {
    if (!limits.enabled) return undefined;
    if (raw && !/^[0-9A-Z]+$/i.test(raw)) {
      return 'O dígito da agência deve conter apenas números';
    }
    if (raw) {
      const bankKey = resolveBankKey(paymentType.description);
      if (bankKey && values.agency) {
        const isValid = validateBankAgencyDigit(
          bankKey,
          values.agency,
          raw.toUpperCase()
        );
        if (isValid === false) {
          return 'Dígito da agência inválido para o banco selecionado.';
        }
      }
    }
    return undefined;
  }

  if (fieldName === 'account') {
    if (!raw) return 'Informe a conta';
    if (!isDigits(raw)) return 'A conta deve conter apenas números';
    return undefined;
  }

  if (!raw) return 'Informe o dígito da conta';
  if (!/^[0-9A-Z]+$/i.test(raw)) {
    return 'O dígito da conta deve conter apenas números';
  }

  const {
    agencyNumberValid,
    agencyValid,
    accountNumberValid,
    accountValid,
  } = validateAccountWithRules(
    rules,
    values.agency,
    values.agencyDigit,
    values.account,
    raw.toUpperCase()
  );

  if (values.agencyDigit && values.accountDigit) {
    if (!agencyNumberValid && fieldName === 'accountDigit') {
      return undefined;
    }
    if (!agencyValid) {
      return undefined;
    }
    if (!accountNumberValid) {
      return undefined;
    }
    if (!accountValid) {
      return 'Dígito da conta inválido para o banco selecionado.';
    }
  }

  const bankKey = resolveBankKey(paymentType.description);
  if (bankKey && values.account) {
    const isValid = validateBankAccountDigit(
      bankKey,
      values.agency,
      values.account,
      raw.toUpperCase()
    );
    if (isValid === false) {
      return 'Dígito da conta inválido para o banco selecionado.';
    }
  }

  return undefined;
}
